/**
 * api.js — Axios client for the Agreements backend.
 *
 * If the app already exposes a shared axios instance with auth interceptors, swap `http` for it.
 * All endpoints sit under the app's existing /api prefix.
 */

import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const http = axios.create({ baseURL: BASE, withCredentials: true });

/** The project/brand intake — one per project. */
export const intakeApi = {
  get: (projectId) => http.get(`/agreements/intake/${projectId}`).then((r) => r.data),
  save: (projectId, intake) => http.put(`/agreements/intake/${projectId}`, intake).then((r) => r.data),
  remove: (projectId) => http.delete(`/agreements/intake/${projectId}`).then((r) => r.data),
  // Every intake this user has submitted, in "My Plan" list order
  // (most recent first). Backend derives `brand_name` from
  // `answers.tradeName` (or legalEntityName fallback).
  listSubmissions: () => http.get(`/agreements/intakes`).then((r) => r.data),
};

/** The generated document plan, attached to a project. */
export const planApi = {
  get: (projectId) => http.get(`/agreements/plan/${projectId}`).then((r) => r.data),
  /** The rules engine lives in lib/agreements/recommend.js; the client posts what it computed. */
  generate: (projectId, plan) => http.post(`/agreements/plan/${projectId}/generate`, plan).then((r) => r.data),
  /** Push the plan onto the founder's project record in the projects module. */
  attach: (projectId) => http.post(`/agreements/plan/${projectId}/attach`).then((r) => r.data),
};

/** Per-document checklist state: signed, counterparty, dates, attorney reviewed, copy stored. */
export const checklistApi = {
  list: (projectId) => http.get(`/agreements/checklist/${projectId}`).then((r) => r.data),
  upsert: (projectId, docId, row) =>
    http.put(`/agreements/checklist/${projectId}/${docId}`, row).then((r) => r.data),
  remove: (projectId, docId) =>
    http.delete(`/agreements/checklist/${projectId}/${docId}`).then((r) => r.data),
};

/** Saved, filled template drafts. */
export const draftsApi = {
  list: (projectId) => http.get(`/agreements/drafts/${projectId}`).then((r) => r.data),
  create: (projectId, payload) => http.post(`/agreements/drafts/${projectId}`, payload).then((r) => r.data),
  remove: (projectId, draftId) => http.delete(`/agreements/drafts/${projectId}/${draftId}`).then((r) => r.data),
};

/** The projects the founder can attach a plan to. Reads the app's existing projects module. */
export const projectsApi = {
  list: () => http.get(`/agreements/projects`).then((r) => r.data),
};

/** Fire-and-forget usage analytics. Never blocks the UI. */
export const eventsApi = {
  track: (event) => http.post(`/agreements/events`, event).catch(() => {}),
};

export const adminApi = {
  stats: () => http.get(`/agreements/admin/stats`).then((r) => r.data),
};

export default { intakeApi, planApi, checklistApi, draftsApi, projectsApi, eventsApi, adminApi };
